// CreerMonAssociation.jsx — Guide officiel "Créer une association loi 1901"
// Direction artistique : éditoriale, magazine — Fraunces + Inter, fond F9F9F8, accent #0047FF
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, Users, FileSignature, Building2, FileCheck,
  ExternalLink, Info, CheckCircle2, Sparkles,
} from 'lucide-react';
import EditorCredit from '../components/EditorCredit';
import { Logo } from '@/components/Logo';

const STEPS = [
  {
    num: 1,
    icon: Users,
    titre: 'Trouvez au moins 2 personnes',
    desc: 'Une association loi 1901 nécessite au minimum 2 fondateurs (membres majeurs ou mineurs avec accord parental). Définissez ensemble l\'objet de l\'asso, son nom et son siège social.',
    duree: '1-2 jours',
    cta: null,
  },
  {
    num: 2,
    icon: FileSignature,
    titre: 'Rédigez les statuts',
    desc: 'Les statuts décrivent : l\'objet, la durée, le siège, les membres, le bureau (président, trésorier, secrétaire), la dissolution… Utilisez un modèle officiel pour ne rien oublier.',
    duree: '2-4 heures',
    cta: { label: 'Modèles officiels Service-Public', url: 'https://www.service-public.fr/associations/vosdroits/F1119' },
  },
  {
    num: 3,
    icon: Building2,
    titre: 'Tenez une assemblée constitutive',
    desc: 'Réunissez les fondateurs, votez les statuts et désignez le bureau. Rédigez un procès-verbal signé par les membres élus.',
    duree: '1-2 heures',
    cta: null,
  },
  {
    num: 4,
    icon: FileCheck,
    titre: 'Déclarez l\'association',
    desc: 'Déclaration en ligne en 15 min sur Service-Public.fr. Vous fournissez : nom, objet, siège, statuts signés, PV de l\'AG constitutive, liste des membres du bureau. Coût : gratuit.',
    duree: '15 min en ligne',
    cta: { label: 'Déclarer mon asso sur Service-Public.fr', url: 'https://www.service-public.fr/compte/se-connecter?targetUrl=/loginSuccessFromSp&typeCompte=association' },
  },
  {
    num: 5,
    icon: FileText,
    titre: 'Publication au Journal Officiel (JOAFE)',
    desc: 'La préfecture transmet automatiquement votre déclaration au Journal Officiel sous 1 mois. Vous recevez un récépissé et un numéro RNA (W…). À partir de là, votre asso a une existence juridique.',
    duree: 'Auto · 1 mois',
    cta: { label: 'Consulter le JOAFE', url: 'https://www.journal-officiel.gouv.fr/pages/associations/' },
  },
];

const APRES = [
  { label: 'Numéro SIRET', desc: 'Demande gratuite à l\'INSEE si vous recevez des subventions publiques ou employez des salariés.', url: 'https://www.insee.fr/fr/information/2017364' },
  { label: 'Compte bancaire associatif', desc: 'Crédit Agricole, Banque Postale, La Nef… Plusieurs banques proposent des comptes pros pour associations.', url: null },
  { label: 'Assurance RC', desc: 'Indispensable dès que vous organisez un événement ou accueillez du public. MAIF, MAAF, etc.', url: null },
  { label: 'Numéro RNA (W…)', desc: 'Reçu après déclaration. Indispensable pour toutes les démarches officielles.', url: null },
];

export default function CreerMonAssociation() {
  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased" data-testid="page-creer-asso">

      {/* NAVBAR */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="creer-asso-back-home">
            <Logo size={30} className="transition-transform group-hover:scale-105" />
            <span className="font-display font-black tracking-tight text-[#0F172A]">AssoPerf</span>
          </Link>
          <Link to="/" className="text-sm font-semibold text-[#475569] hover:text-[#0F172A] transition-colors">
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* HERO ÉDITORIAL */}
      <section className="relative pt-16 sm:pt-24 pb-12 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10" aria-hidden>
          <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#0047FF]/5 blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0047FF]/10 text-[#0047FF] text-xs font-bold uppercase tracking-[0.18em] mb-7" data-testid="creer-asso-badge">
            <Info className="w-3.5 h-3.5" />
            Guide officiel
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight text-[#0F172A] leading-[0.98] mb-6" data-testid="creer-asso-title">
            Créer une <span className="italic font-medium text-[#0047FF]">association</span> en France.
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] leading-relaxed max-w-2xl">
            En 5 étapes officielles, vous obtenez le statut d'association loi 1901.{' '}
            <strong className="text-[#0F172A] font-semibold">AssoPerf n'effectue pas la création juridique</strong> — on vous guide vers les bons services publics et on prend le relais pour la gestion au quotidien.
          </p>
        </div>
      </section>

      {/* AVERTISSEMENT — bandeau sobre */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 -mt-4 mb-12">
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] rounded-r-xl p-5 flex items-start gap-3 shadow-sm" data-testid="creer-asso-warning">
          <Info className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
          <div className="text-sm text-[#78350F] leading-relaxed">
            <strong className="text-[#0F172A]">Important :</strong> AssoPerf est un <strong>outil de gestion</strong> pour les associations déjà déclarées en préfecture. Nous ne pouvons pas créer juridiquement votre association à votre place — c'est une démarche officielle (et gratuite) que vous devez effectuer vous-même via Service-Public.fr.
          </div>
        </div>
      </section>

      {/* ÉTAPES — timeline éditoriale */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-3">Les 5 étapes</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] leading-[1.05] mb-12">
          De l'idée <span className="italic font-medium text-[#0047FF]">au récépissé.</span>
        </h2>

        <div className="space-y-2" data-testid="creer-asso-steps">
          {STEPS.map((s, i) => {
            const Ic = s.icon;
            const isLast = i === STEPS.length - 1;
            return (
              <div key={s.num} className="flex gap-5 sm:gap-7" data-testid={`step-${s.num}`}>
                {/* Colonne numéro + ligne */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-12 h-12 rounded-2xl bg-[#0F172A] text-white grid place-items-center font-display font-black text-lg shadow-md">
                    {s.num}
                  </div>
                  {!isLast && <div className="w-px flex-1 bg-[#E2E8F0] mt-3 mb-3 min-h-[24px]" />}
                </div>

                {/* Carte étape */}
                <div className={`flex-1 min-w-0 ${isLast ? 'pb-2' : 'pb-10'}`}>
                  <div className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7 transition-all hover:shadow-lg hover:-translate-y-0.5">
                    <div className="flex items-center gap-2.5 flex-wrap mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#0047FF]/10 text-[#0047FF] grid place-items-center">
                        <Ic className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">{s.duree}</span>
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A] leading-tight mb-2.5">{s.titre}</h3>
                    <p className="text-sm sm:text-base text-[#475569] leading-relaxed">{s.desc}</p>
                    {s.cta && (
                      <a
                        href={s.cta.url}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-[#0047FF] hover:underline underline-offset-4"
                        data-testid={`step-${s.num}-cta`}
                      >
                        {s.cta.label}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* APRÈS — démarches complémentaires */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-xl mb-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-3">Et après ?</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] leading-[1.05]">
              Démarches <span className="italic font-medium text-[#0047FF]">complémentaires.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="creer-asso-apres">
            {APRES.map((a) => (
              <div key={a.label} className="bg-[#F9F9F8] border border-[#E2E8F0] rounded-2xl p-5 transition-all hover:bg-white hover:shadow-sm">
                <h3 className="flex items-center gap-2 font-display text-base font-bold text-[#0F172A] mb-2">
                  <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" /> {a.label}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed">{a.desc}</p>
                {a.url && (
                  <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2.5 text-xs font-bold text-[#0047FF] hover:underline underline-offset-4">
                    En savoir plus <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="relative bg-[#0A192F] rounded-3xl overflow-hidden p-10 sm:p-14 text-center text-white">
            <div className="absolute inset-0 -z-0 opacity-30" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(0,71,255,0.45), transparent 50%)' }} />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#4DFFD2] text-[10px] font-bold uppercase tracking-[0.2em] mb-5">
                <Sparkles className="w-3 h-3" /> Votre asso est déclarée ?
              </div>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight mb-5 max-w-2xl mx-auto">
                Place à <span className="italic font-medium text-[#4DFFD2]">la gestion.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-9 leading-relaxed">
                Une fois votre numéro RNA en poche, créez votre espace AssoPerf en 2 minutes : site public, app membre, comptabilité, paiements, agents IA — tout sous votre marque.
              </p>
              <Link
                to="/"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white hover:bg-[#0047FF] hover:text-white text-[#0F172A] font-bold transition-all shadow-xl hover:-translate-y-0.5"
                data-testid="creer-asso-final-cta"
              >
                Créer mon espace de gestion
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <div className="mt-6 text-xs text-white/50">
                14 jours d'essai gratuits · Aucune carte requise · Annulation en 1 clic
              </div>
            </div>
          </div>
        </div>
      </section>

      <EditorCredit variant="light" />
    </div>
  );
}
